// Barra di navigazione unica per le pagine pubbliche (home, chi siamo, novita',
// piani, accesso).
//
// PERCHE' ESISTE: fino a oggi la nav era copiata in tre file (HomePage 97
// righe, AboutPage 33, NewsPage 31) e le tre copie erano gia' divergenti —
// About e Novita' non avevano ne' EN/IT ne' il selettore di tema, Novita' non
// aveva "Chi siamo", nessuna delle due aveva "Roadmap". Ogni modifica alla nav
// andava fatta tre volte, e infatti due volte su tre non e' stata fatta: le
// emoji tolte dalla home il 2026-09-02 erano rimaste in entrambe le altre.
//
// NAV CANONICA (decisa 2026-09-02): otto voci, nessuna sparisce. Roadmap e
// Chi siamo entrano ovunque. Accorciare la nav e' una decisione a se', da
// misurare, non un effetto collaterale di un accorpamento.
//
// COSA NON CAMBIA: la classe cps-nav-home resta applicata solo alla home,
// perche' una regola in App.css (riga 98) nasconde "Registrati" sopra i 701px
// proprio li' — il modulo email della hero e' l'unica CTA di registrazione
// della pagina. Quella scelta e' di conversione, non di stile: il componente
// condiviso la conserva invece di cancellarla in silenzio.

// IL DIZIONARIO DELLA NAV E' SUO (2026-09-03). Prima `t` arrivava come PROP
// dalla pagina che montava la nav, quindi la nav leggeva il namespace della
// PAGINA: 'home' sulla home, 'about' su Chi siamo, 'news' su Novita'. Le stesse
// dodici stringhe erano quindi copiate in tre dizionari per lingua — sei file —
// e bastava dimenticarne una in uno solo perche' quella voce stampasse il nome
// grezzo della chiave. E' successo: nav_about esisteva in home.json e in
// about.json ma NON in news.json, ne' in italiano ne' in inglese, e su /news la
// voce mostrava "nav_about". Aggiungere la chiave a news.json avrebbe rimesso a
// posto quella pagina e lasciato in piedi il difetto, pronto a ripresentarsi
// alla prossima pagina o alla prossima voce.
//
// Ora le stringhe stanno in un namespace 'nav' (i18n/locales/{it,en}/nav.json),
// caricato da chiunque monti questo componente. Un solo posto per lingua: una
// voce che manca manca ovunque, e si vede subito, invece di mancare su una
// pagina sola. Il prop `t` non esiste piu': non e' un dettaglio interno, era
// proprio la via per cui il difetto entrava.
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageMenu from './LanguageMenu';
import ThemeToggle from './ThemeToggle';

// Le otto voci nell'ordine deciso. Definite una volta sola e usate sia dalla
// barra sia dal pannello del telefono: due elenchi scritti a mano sono
// esattamente come le tre nav sono andate fuori sincrono.
//
// tipo: 'sezione' -> ancora nella home (scorrimento o navigazione + hash)
//       'pagina'  -> pagina propria
// soloMenu: sta nel menu del telefono ma non nella barra larga.
//
// PERCHE' DUE VOCI NON SONO NELLA BARRA (2026-09-02, misurato non stimato):
// .cps-nav-inner e' largo al massimo 1100px, a qualunque risoluzione. Con otto
// voci il contenuto naturale ne chiede 1157 in italiano e 1117 in inglese:
// margine -57px e -17px. Non andava a capo solo perche' white-space:nowrap lo
// impediva, e la parte in eccesso sbordava in silenzio nei margini laterali
// della barra — invisibile a 1440px, addosso al bordo a 1181px, dove la barra
// larga viene mostrata per l'ultima volta.
// Restringere ancora corpo e spaziatura avrebbe solo spostato il punto di
// rottura di una voce. Roadmap e Chi siamo restano raggiungibili dal menu, con
// le stesse etichette: nessuna voce e' stata cancellata, sei nella barra e otto
// nel menu.
//
// `riserva`: etichetta leggibile da usare se la traduzione manca in TUTTE e due
// le lingue. Vedi etichettaDi() qui sotto — nessuna voce di questa barra deve
// poter stampare "nav_qualcosa" addosso a un visitatore.
const VOCI = [
  { chiave: 'nav_how_it_works', tipo: 'sezione', id: 'how-it-works', riserva: 'How it works' },
  { chiave: 'nav_plans',        tipo: 'sezione', id: 'plans',        riserva: 'Plans' },
  { chiave: 'nav_security',     tipo: 'sezione', id: 'security',     riserva: 'Security' },
  { chiave: 'nav_roadmap',      tipo: 'sezione', id: 'roadmap', classe: 'cps-nav-roadmap', riserva: 'Roadmap' },
  { chiave: 'nav_faq',          tipo: 'sezione', id: 'faq',          riserva: 'FAQ' },
  { chiave: 'nav_about',        tipo: 'pagina',  pagina: 'about',    riserva: 'About Us' },
  { chiave: 'nav_news',         tipo: 'pagina',  pagina: 'news', pastiglia: 'NEW', riserva: 'News' },
  { chiave: 'nav_contact',      tipo: 'sezione', id: 'contact',      riserva: 'Contact us' },
];

// RETE DI SICUREZZA, chiesta esplicitamente il 2026-09-03: se una chiave manca,
// la nav non stampa mai il nome della chiave.
//
// i18next, quando non trova nulla, restituisce la CHIAVE. E' un ripiego buono
// per chi sviluppa e pessimo per chi visita: al posto di "Chi siamo" compare
// "nav_about", che sembra un sito rotto. Il ripiego di i18next non basta da
// solo: fallbackLng riporta all'inglese, ma se la chiave manca su ENTRAMBI i
// lati — il caso capitato — non ha niente a cui ripiegare.
//
// Ordine: traduzione -> etichetta di riserva scritta qui -> voce nascosta.
// Il terzo caso non e' un fallimento silenzioso: una voce senza nome non e'
// cliccabile ne' leggibile, e una barra con sette voci giuste e' meglio di una
// con otto di cui una illeggibile. In pratica non dovrebbe mai accadere,
// perche' ogni voce di VOCI ha la sua riserva.
function etichettaDi(t, voce) {
  const tradotta = t(voce.chiave, { defaultValue: '' });
  const buona = typeof tradotta === 'string'
    && tradotta.trim() !== ''
    && tradotta !== voce.chiave;
  if (buona) return tradotta;
  return (voce.riserva || '').trim() || null;
}

// Una voce, usata identica nella barra e nel pannello del telefono.
function Voce({ voce, t, attiva, onClick }) {
  // La voce della pagina corrente e' VERDE, non in grassetto (2026-09-02).
  // Il grassetto la allargava di ~50px, quindi la larghezza della barra
  // cambiava da pagina a pagina: su Chi siamo "Chi siamo" passava da 89 a
  // 139px e la barra sforava solo li'. Il colore e aria-current dicono gia'
  // dove ci si trova, e la geometria della barra non deve dipendere dalla
  // pagina che si sta guardando.
  const classi = [voce.classe, attiva ? 'attiva' : ''].filter(Boolean).join(' ');
  // Nessuna etichetta leggibile: la voce non si disegna. Vedi etichettaDi().
  const etichetta = etichettaDi(t, voce);
  if (!etichetta) return null;
  return (
    <button
      type="button"
      className={classi || undefined}
      aria-current={attiva ? 'page' : undefined}
      onClick={onClick}
    >
      {etichetta}
      {/* Pastiglia testuale, non 🆕: l'emoji veniva disegnata a colori fissi da
          ogni piattaforma, quindi ignorava il token del tema, e cambiava
          larghezza rompendo l'allineamento della barra. "NEW" resta in inglese
          anche in italiano: e' l'uso corrente e sono tre lettere riconoscibili,
          non una parola da tradurre. aria-hidden perche' e' decorazione: il
          nome accessibile del bottone resta "Novita'". */}
      {/* La pastiglia non compare sulla voce della pagina in cui gia' ci si
          trova: li' e' informazione ripetuta ("sei su Novita'" lo dicono gia'
          il colore e aria-current), e costa 40px di barra proprio sulla pagina
          dove la barra e' piu' larga, perche' la voce corrente e' anche in
          grassetto. Misurato: Novita' in italiano a 1280px passa da +27 a +67. */}
      {voce.pastiglia && !attiva && (
        <span className="cps-pill-new" aria-hidden="true">{voce.pastiglia}</span>
      )}
    </button>
  );
}

export default function SiteNav({
  pagina = 'home',      // 'home' | 'about' | 'news' | 'plans' | 'auth'
  onVaiA,               // (idSezione) => void — scorre (home) o naviga con hash (altrove)
  onAbout,
  onNews,
  onLogin,
  onRegister,
  tema,
  onCambiaTema,
}) {
  // Namespace proprio: non dipende piu' da quale pagina monta la nav.
  const { t, i18n } = useTranslation('nav');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Bug vero, segnalato dal vivo (2026-08-29): la nav fissa copriva il titolo
  // della hero quando il banner promozionale veniva chiuso. Si misura
  // l'altezza reale invece di indovinarla, cosi' regge se cambiano hamburger,
  // logo o padding — vedi il padding-top di .cps-hero basato su --nav-h.
  // Stava in HomePage: qui vale per tutte le pagine che usano la nav.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const update = () => root.style.setProperty('--nav-h', `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { ro.disconnect(); root.style.removeProperty('--nav-h'); };
  }, []);

  // I LINK CEDONO PER PRIMI, IL BOTTONE DI ACCESSO MAI (2026-09-17).
  //
  // La regola fissa a 1264px (piu' sotto nel CSS) e' stata scritta con due
  // lingue e otto voci: con cinque lingue e i testi lunghi di spagnolo e
  // francese, a 1280-1440 le voci non ci stanno e il bottone «Accedi»
  // finiva fuori dallo schermo. Qui si MISURA: se logo + voci + blocco di
  // destra non entrano nel contenitore, le voci passano nel menu a panino
  // (classe cps-nav-compatta) e il bottone resta intero al suo posto. Le
  // voci nascoste restano nel DOM, invisibili e fuori flusso, cosi' la loro
  // larghezza si puo' rimisurare quando cambia la lingua o la finestra.
  // Si rimisura al cambio lingua, al ridimensionamento e quando arrivano i
  // font (Syne e' piu' largo del carattere di riserva).
  const [compatta, setCompatta] = useState(false);
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const misura = () => {
      const inner = nav.querySelector('.cps-nav-inner');
      const logo = nav.querySelector('.cps-logo');
      const links = nav.querySelector('.cps-nav-links');
      const auth = nav.querySelector('.cps-nav-auth');
      if (!inner || !logo || !links || !auth) return;
      // Larghezze frazionarie, non offsetWidth arrotondato: con otto voci
      // gli arrotondamenti sommati sforavano di 1px un contenitore in cui
      // tutto stava, e l'inglese finiva nel panino a 1440.
      const gap = parseFloat(getComputedStyle(inner).gap) || 0;
      const gapVoci = parseFloat(getComputedStyle(links).gap) || 0;
      const voci = Array.from(links.children);
      const larghezzaVoci = voci.reduce((tot, v) => tot + v.getBoundingClientRect().width, 0) + gapVoci * Math.max(0, voci.length - 1);
      const serve = logo.getBoundingClientRect().width + larghezzaVoci + auth.getBoundingClientRect().width + gap * 2;
      setCompatta(serve > inner.getBoundingClientRect().width + 1);
    };
    misura();
    const ro = new ResizeObserver(misura);
    ro.observe(nav);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(misura).catch(() => {});
    return () => ro.disconnect();
  }, [i18n.language]);

  // Il pannello del menu parte SOTTO la fascia dell'offerta (position:fixed,
  // z-index sopra il pannello): a top:0 finiva coperto e la X non era
  // cliccabile (2026-09-05). Si misura l'altezza REALE della fascia — non un
  // valore fisso — cosi' regge se e' visibile, chiusa (allora e' 0 e il
  // pannello risale) o andata a capo su due righe a 360px. La fascia e'
  // sticky top:0, quindi il suo bordo inferiore in viewport E' la sua altezza.
  const [fasciaGiu, setFasciaGiu] = useState(0);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const misura = () => {
      const f = document.querySelector('.ltc-barra-offerta');
      const giu = f ? Math.max(0, Math.round(f.getBoundingClientRect().bottom)) : 0;
      setFasciaGiu(giu);
    };
    misura();
    const ro = new ResizeObserver(misura);
    const f = document.querySelector('.ltc-barra-offerta');
    if (f) ro.observe(f);
    window.addEventListener('resize', misura);
    return () => { ro.disconnect(); window.removeEventListener('resize', misura); };
  }, [menuOpen]);

  // Esc chiude il pannello, e lo scorrimento della pagina resta bloccato solo
  // finche' e' aperto. Il ripristino sta nella pulizia, quindi vale anche se il
  // componente viene smontato con il pannello aperto: senza, la pagina
  // resterebbe bloccata e l'unico rimedio sarebbe ricaricare.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const suTasto = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    const precedente = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', suTasto);
    return () => {
      document.removeEventListener('keydown', suTasto);
      document.body.style.overflow = precedente;
    };
  }, [menuOpen]);

  function chiudiE(fn) {
    return (...args) => { setMenuOpen(false); if (fn) fn(...args); };
  }

  function azione(voce) {
    if (voce.tipo === 'sezione') return chiudiE(() => onVaiA(voce.id));
    if (voce.pagina === 'about') return chiudiE(onAbout);
    return chiudiE(onNews);
  }

  // La voce della pagina in cui ci si trova: verde e in grassetto, come oggi su
  // About e Novita'. aria-current la dice anche a chi non vede il colore.
  function corrente(voce) {
    return voce.tipo === 'pagina' && voce.pagina === pagina;
  }

  // Definita fuori dal corpo del render (vedi Voce piu' sotto): un componente
  // dichiarato dentro un altro viene ricreato a ogni render e React ne
  // rimonta il DOM, perdendo il fuoco da tastiera a ogni scorrimento della
  // pagina — e la barra si ridisegna a ogni scroll per via di `scrolled`.
  const voceProps = (voce) => ({
    voce,
    t,
    attiva: corrente(voce),
    onClick: azione(voce),
  });

  return (
    <nav ref={navRef} className={`cps-nav${pagina === 'home' ? ' cps-nav-home' : ''}${scrolled ? ' scrolled' : ''}${compatta ? ' cps-nav-compatta' : ''}`}>
      <div className="cps-nav-inner">
        <div className="cps-logo" onClick={chiudiE(() => onVaiA('hero'))}>
          {/* Niente emoji nella barra (2026-09-02): il simbolo dell'infinito
              veniva reso in modo diverso da ogni piattaforma e a colori fissi,
              quindi ignorava il token del tema. SVG con currentColor. */}
          <svg className="cps-logo-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
            <path d="M6.6 8.4a3.6 3.6 0 1 0 0 7.2c2.6 0 3.2-1.9 5.4-3.6 2.2-1.7 2.8-3.6 5.4-3.6a3.6 3.6 0 1 1 0 7.2c-2.6 0-3.2-1.9-5.4-3.6C9.8 10.3 9.2 8.4 6.6 8.4z" />
          </svg>
          <span className="cps-logo-text">LifetimeCloud</span>
        </div>

        <div className="cps-nav-links">
          {VOCI.map((v) => <Voce key={v.chiave} {...voceProps(v)} />)}
        </div>

        <div className="cps-nav-auth">
          <LanguageMenu />
          <ThemeToggle tema={tema} onCambia={onCambiaTema} />
          <button className="cps-btn-login" onClick={chiudiE(onLogin)}>{t('nav_login', 'Login')}</button>
          {/* "Registrati" NON sta nella barra, su nessuna pagina (2026-09-02).
              Sulla home era gia' cosi' dal 29 agosto: l'unica CTA di
              registrazione della pagina e' il modulo email della hero. Ora vale
              anche per Chi siamo e Novita', per una ragione misurata: con quel
              bottone (131px + 28 di spaziatura) la barra sfora il contenitore
              su quelle due pagine, fino a -114px a 1280 di viewport.
              L'invito a registrarsi NON sparisce: sta nel blocco di chiusura in
              fondo a ciascuna delle due pagine, stesso bottone verde pieno e
              stessa destinazione. Nel menu del telefono resta dov'era. */}
        </div>

        {/* Tre linee disegnate, non il carattere ☰: quello cambia larghezza e
            allineamento su ogni piattaforma e non segue currentColor. */}
        <button
          type="button"
          className="cps-hamburger"
          aria-label={t('nav_menu', 'Menu')}
          aria-expanded={menuOpen}
          aria-controls="cps-pannello-menu"
          onClick={() => setMenuOpen(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
      </div>

      {/* Pannello laterale (2026-09-02). Prima era un blocco a larghezza piena
          che copriva tutto: aperto, del sito non si vedeva piu' niente e non si
          capiva di essere ancora nella stessa pagina. Ora e' un pannello a
          destra su un velo semitrasparente. Resta nel DOM anche da chiuso,
          altrimenti la transizione di entrata non avrebbe uno stato di partenza
          da cui animare. */}
      {/* Guscio fisso a tutto schermo con overflow:clip: il pannello sta
          DENTRO ed e' position:absolute, cosi' da chiuso (translateX(100%),
          fuori a destra) viene ritagliato dal guscio e NON allarga il
          documento — su telefono lo scroll orizzontale fantasma nasceva
          proprio dal pannello fisso spinto oltre il bordo destro. Il clip
          sta sul contenitore del pannello, mai su body/html (romperebbe la
          fascia ancorata). */}
      <div className={`cps-menu-guscio${menuOpen ? ' aperto' : ''}`}>
      <div
        className={`cps-menu-velo${menuOpen ? ' aperto' : ''}`}
        style={{ top: fasciaGiu }}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <div
        id="cps-pannello-menu"
        className={`cps-menu-pannello${menuOpen ? ' aperto' : ''}`}
        style={{ top: fasciaGiu }}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav_menu', 'Menu')}
        aria-hidden={!menuOpen}
      >
        <div className="cps-menu-testa">
          {/* Tema e lingua restano raggiungibili anche da telefono: nella barra
              non ci stanno, quindi vivono qui in cima invece di sparire. */}
          <LanguageMenu className="cps-mobile-lang" />
          <ThemeToggle tema={tema} onCambia={onCambiaTema} />
          <button
            type="button"
            className="cps-menu-chiudi"
            aria-label={t('nav_close', 'Chiudi')}
            onClick={() => setMenuOpen(false)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className="cps-menu-voci">
          {VOCI.map((v) => <Voce key={v.chiave} {...voceProps(v)} />)}
        </div>
        <div className="cps-menu-fondo">
          <button className="cps-btn-login" onClick={chiudiE(onLogin)}>{t('nav_login', 'Login')}</button>
          <button className="cps-btn-register" onClick={chiudiE(onRegister)}>{t('nav_register', 'Register')}</button>
        </div>
      </div>
      </div>
    </nav>
  );
}
